/**
 * @fileoverview State, image selection, and submit handling for RequirementCreationView.
 * @module components/RequirementCreationView/useRequirementCreationForm
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { logger } from "../../utils/logger.js";
import {
  areRequirementCreateMandatoryFieldsComplete,
  handleCreateRequirement,
} from "../../logic/requirements";
import { useFieldValidation } from "../../hooks/useFieldValidation";
import styles from "./index.module.css";

function createEmptyRequirement() {
  return {
    idCategoria: null,
    nombreSubcategoria: null,
    titulo: null,
    fechaLimite: null,
    descripcion: null,
    ubicacion: null,
    sector: null,
    subSector: null,
    rama: null,
    certificaciones: [],
    image: null,
    volumen: null,
    experienciaMinima: null,
  };
}

function logRequirementCreationMounted() {
  logger.info("RequirementCreationView mounted");
  // if (open) {
  //   setRequirement({
  //     idCategoria: null,
  //     nombreSubcategoria: null,
  //     titulo: null,
  //     fechaLimite: null,
  //     descripcion: null,
  //     ubicacion: null,
  //     sector: null,
  //     subSector: null,
  //     rama: null,
  //     certificaciones: [],
  //     image: null,
  //     volumen: null,
  //   });
  //   setCategories([]);
  //   setSubcategories([]);
  //   setSectors([]);
  //   setSubsectors([]);
  //   setBranches([]);
  //   setCertifications([]);
  //   setOriginalCertifications([]);
  //   setTempImage(null);
  //   setImageFormat(null);

  //   // Clear any input fields if needed
  //   if (selectCategoryRef.current) selectCategoryRef.current.clearValue();
  //   if (selectSubcategoryRef.current)
  //     selectSubcategoryRef.current.clearValue();
  //   if (selectSectorRef.current) selectSectorRef.current.clearValue();
  //   if (selectSubsectorRef.current) selectSubsectorRef.current.clearValue();
  //   if (selectBranchRef.current) selectBranchRef.current.clearValue();

  // }
}

function applyRequirementImageFile(
  event,
  { t, setImageError, setTempImage, setImageFile, setImageName, setImageFormat },
) {
  const file = event.target.files[0];
  if (!file) {
    return;
  }
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
  const maxSize = 5 * 1024 * 1024;
  if (!allowedTypes.includes(file.type)) {
    setImageError(t("pages.buyerCompanyForm.logoFileTypeError"));
    setTempImage(null);
    setImageFile(null);
    setImageName(null);
    setImageFormat(null);
    return;
  }
  if (file.size > maxSize) {
    setImageError(t("pages.buyerCompanyForm.logoFileSizeError"));
    setTempImage(null);
    setImageFile(null);
    setImageFormat(null);
    setImageName(null);
    return;
  }
  setImageError("");
  setImageFile(file);
  setImageName(file.name);
  setImageFormat(file.type);
  const reader = new FileReader();
  reader.onload = (e) => {
    setTempImage(e.target.result);
  };
  reader.readAsDataURL(file);
}

export function useRequirementCreationForm({ onCreateRequirement, onBack }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selectCategoryRef = useRef(null);
  const selectSubcategoryRef = useRef(null);
  const selectSectorRef = useRef(null);
  const selectSubsectorRef = useRef(null);
  const selectBranchRef = useRef(null);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    requerimientoTitulo: "",
    requerimientoDescripcion: "",
    requerimientoUbicacion: "",
    certificacionExperiencia: "",
    requerimientoVolumen: "",
    requerimientoExperienciaMinima: "",
  });

  const [subcategories, setSubcategories] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [tempImage, setTempImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requirement, setRequirement] = useState(createEmptyRequirement());

  const areMandatoryFieldsComplete = useMemo(
    () => areRequirementCreateMandatoryFieldsComplete(requirement),
    [requirement],
  );

  const imageRef = useRef(null);
  const onDragEnter = (e) => imageRef.current.classList.add(styles.dragOver);
  const onDragLeave = (e) => imageRef.current.classList.remove(styles.dragOver);
  const onDrop = (e) => imageRef.current.classList.remove(styles.dragOver);

  const handleSubmitRequirement = async () => {
    if (!areMandatoryFieldsComplete) {
      return;
    }
    setIsSubmitting(true);
    try {
      await handleCreateRequirement({
        requirement,
        imageFile,
        nombreImagen: imageFile ? imageName : null,
        formatoImagen: imageFile ? imageFormat : null,
        navigate,
        onCreateRequirement,
        onBack,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    logRequirementCreationMounted();
  }, []);

  const handleChange = (field, value) => {
    setRequirement((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeImage = (event) => {
    applyRequirementImageFile(event, {
      t,
      setImageError,
      setTempImage,
      setImageFile,
      setImageName,
      setImageFormat,
    });
  };

  return {
    t,
    selectCategoryRef,
    selectSubcategoryRef,
    selectSectorRef,
    selectSubsectorRef,
    selectBranchRef,
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
    subcategories,
    setSubcategories,
    subSectors,
    setSubsectors,
    branches,
    setBranches,
    certifications,
    setCertifications,
    tempImage,
    imageError,
    imageRef,
    onDragEnter,
    onDragLeave,
    onDrop,
    handleChangeImage,
    requirement,
    setRequirement,
    handleChange,
    areMandatoryFieldsComplete,
    isSubmitting,
    handleSubmitRequirement,
  };
}
